import type { ReactNode } from 'react';
import { Icone, type NomeDeIcone } from './Icone.js';

/**
 * Os componentes de apresentação do protótipo.
 *
 * Cada um é casca tipada sobre as classes de `styles/componentes.css`:
 * dá nome, props e semântica; a aparência vem do CSS aprovado. Escrever
 * `<Status situacao="atrasada">` em vez de
 * `<span className="status s-late">` não é açúcar — é o que permite
 * mudar a regra de "o que conta como atrasada" num lugar só.
 *
 * Ver: Arena_backend/Docs/plano/05-front-fundacao.md
 */

// ---------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------

export function Card({
  titulo,
  dica,
  acao,
  chapado,
  className,
  children,
}: {
  titulo?: ReactNode;
  dica?: ReactNode;
  acao?: ReactNode;
  /** Sem sombra — para cards dentro de cards. */
  chapado?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={['card', chapado && 'flat', className].filter(Boolean).join(' ')}>
      {(titulo || acao || dica) && (
        <header className="card-head">
          {titulo && <h2>{titulo}</h2>}
          {dica && <span className="hint">{dica}</span>}
          {acao}
        </header>
      )}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------
// KPI
// ---------------------------------------------------------------------

export type Tendencia = 'alta' | 'baixa' | 'estavel';

const CLASSE_DE_TENDENCIA: Record<Tendencia, string> = {
  alta: 'up',
  baixa: 'down',
  estavel: 'flat-t',
};

/**
 * O KPI é clicável no protótipo, e isso não é enfeite: cada um abre o
 * drill-down com a memória de cálculo (`#m-kpi`). Um número sem
 * explicação em painel executivo vira discussão sobre o número.
 */
export function Kpi({
  valor,
  rotulo,
  icone,
  tendencia,
  variacao,
  onClick,
}: {
  valor: ReactNode;
  rotulo: ReactNode;
  icone?: NomeDeIcone;
  tendencia?: Tendencia;
  variacao?: ReactNode;
  onClick?: () => void;
}) {
  const interativo = Boolean(onClick);
  return (
    <article
      className="card kpi"
      onClick={onClick}
      onKeyDown={(e) => {
        if (interativo && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      role={interativo ? 'button' : undefined}
      tabIndex={interativo ? 0 : undefined}
    >
      <div className="row">
        <div>
          <div className="val num">{valor}</div>
          <div className="lbl">{rotulo}</div>
        </div>
        {icone && (
          <span className="mark-ic">
            <Icone nome={icone} />
          </span>
        )}
      </div>
      {tendencia && (
        <span className={`trend ${CLASSE_DE_TENDENCIA[tendencia]}`}>
          {tendencia !== 'estavel' && <Icone nome={tendencia === 'alta' ? 'up' : 'down'} />}
          {variacao}
        </span>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------

export type Situacao = 'ok' | 'aguardando' | 'atrasada' | 'atencao' | 'neutra';

const CLASSE_DE_SITUACAO: Record<Situacao, string> = {
  ok: 's-ok',
  aguardando: 's-wait',
  atrasada: 's-late',
  atencao: 's-warn',
  neutra: 's-off',
};

export function Status({ situacao, children }: { situacao: Situacao; children: ReactNode }) {
  return <span className={`status ${CLASSE_DE_SITUACAO[situacao]}`}>{children}</span>;
}

// ---------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------

/**
 * As iniciais vêm do nome. O avatar é neutro de propósito: a cor da
 * interface é do teal que marca seleção e dado, e uma lista de pessoas
 * coloridas competiria com ela. Quem precisa de destaque (o usuário
 * logado, no menu) ganha o tom do teal por CSS.
 */
export function Avatar({
  nome,
  tamanho,
  className,
}: {
  nome: string;
  tamanho?: number;
  className?: string;
}) {
  const iniciais = nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? '')
    .join('');

  const estilo: React.CSSProperties | undefined = tamanho
    ? { width: tamanho, height: tamanho, fontSize: tamanho * 0.36 }
    : undefined;

  return (
    <span className={['av', className].filter(Boolean).join(' ')} style={estilo} aria-hidden="true">
      {iniciais}
    </span>
  );
}

// ---------------------------------------------------------------------
// Botões
// ---------------------------------------------------------------------

export function Botao({
  variante = 'primario',
  pequeno,
  icone,
  children,
  ...resto
}: {
  variante?: 'primario' | 'fantasma' | 'contorno';
  pequeno?: boolean;
  icone?: NomeDeIcone;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const classe = {
    primario: 'btn',
    fantasma: 'btn ghost',
    contorno: 'btn line',
  }[variante];

  return (
    <button
      type="button"
      className={[classe, pequeno && 'sm'].filter(Boolean).join(' ')}
      {...resto}
    >
      {icone && <Icone nome={icone} />}
      {children}
    </button>
  );
}

/** Ícone sozinho, redondo. O `aria-label` é obrigatório de propósito. */
export function BotaoDeIcone({
  icone,
  'aria-label': rotulo,
  comAviso,
  ...resto
}: {
  icone: NomeDeIcone;
  'aria-label': string;
  /** O ponto de notificação pendente. */
  comAviso?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className="icon-btn" aria-label={rotulo} {...resto}>
      <Icone nome={icone} />
      {comAviso && <span className="dot" />}
    </button>
  );
}

// ---------------------------------------------------------------------
// Segmentado, escolhas e pills
// ---------------------------------------------------------------------

export function Segmentado<T extends string>({
  opcoes,
  valor,
  onChange,
  'aria-label': rotulo,
}: {
  opcoes: readonly { valor: T; rotulo: string }[];
  valor: T;
  onChange: (valor: T) => void;
  'aria-label': string;
}) {
  return (
    <div className="seg" role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          aria-pressed={o.valor === valor}
          onClick={() => onChange(o.valor)}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/**
 * `data-single` no protótipo distingue escolha única de múltipla. Aqui
 * a distinção é o tipo: `Escolhas` com `valor: T` é única, com
 * `valor: T[]` é múltipla.
 */
export function Escolhas<T extends string>({
  opcoes,
  valor,
  onChange,
  'aria-label': rotulo,
}: {
  opcoes: readonly { valor: T; rotulo: string }[];
  valor: T | readonly T[];
  onChange: (valor: T) => void;
  'aria-label': string;
}) {
  const selecionados = Array.isArray(valor) ? valor : [valor as T];
  return (
    <div className="choices" role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          className="choice"
          aria-pressed={selecionados.includes(o.valor)}
          onClick={() => onChange(o.valor)}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className="tagx">{children}</span>;
}

export function LinhaDePills({ children }: { children: ReactNode }) {
  return <div className="pill-row">{children}</div>;
}

// ---------------------------------------------------------------------
// Nota, vazio, mini
// ---------------------------------------------------------------------

/**
 * O bloco explicativo do "modo apresentação" do protótipo. Some quando
 * `body` tem a classe `no-notes` — o interruptor fica no rodapé da
 * sidebar.
 */
export function Nota({ children }: { children: ReactNode }) {
  return (
    <div className="note">
      <Icone nome="info" />
      <div>{children}</div>
    </div>
  );
}

export function Vazio({ children, acao }: { children: ReactNode; acao?: ReactNode }) {
  return (
    <div className="empty">
      <div>{children}</div>
      {acao && <div style={{ marginTop: 14 }}>{acao}</div>}
    </div>
  );
}

/** Três métricas lado a lado, dentro de um card ou modal. */
export function Mini({ itens }: { itens: readonly { valor: ReactNode; rotulo: string }[] }) {
  return (
    <div className="mini">
      {itens.map((i) => (
        <div key={i.rotulo}>
          <b className="num">{i.valor}</b>
          <span>{i.rotulo}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Feed e tabela
// ---------------------------------------------------------------------

export function Feed({ children }: { children: ReactNode }) {
  return <ul className="feed">{children}</ul>;
}

export function ItemDeFeed({
  marca,
  titulo,
  subtitulo,
  direita,
  onClick,
}: {
  /** O quadradinho colorido, ou um avatar. */
  marca?: ReactNode;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  direita?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <li
      className={onClick ? 'clickable' : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {marca}
      <div>
        <b>{titulo}</b>
        {subtitulo && <small>{subtitulo}</small>}
      </div>
      {direita && <div className="rt">{direita}</div>}
    </li>
  );
}

export function Tabela({
  colunas,
  children,
}: {
  colunas: readonly ReactNode[];
  children: ReactNode;
}) {
  return (
    <div className="tbl-scroll sb">
      <table className="tbl">
        <thead>
          <tr>
            {colunas.map((c, i) => (
              <th key={i}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** Nome com avatar, o padrão de toda coluna de pessoa. */
export function Quem({ nome, detalhe }: { nome: string; detalhe?: ReactNode }) {
  return (
    <span className="who">
      <Avatar nome={nome} />
      <span>
        <b style={{ display: 'block', fontWeight: 600 }}>{nome}</b>
        {detalhe && <small style={{ color: 'var(--dim)', fontSize: 11.5 }}>{detalhe}</small>}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------

export function Formulario({
  colunas = 1,
  children,
  ...resto
}: { colunas?: 1 | 2 | 3 } & React.FormHTMLAttributes<HTMLFormElement>) {
  const classe = ['form', colunas === 2 && 'f2', colunas === 3 && 'f3'].filter(Boolean).join(' ');
  return (
    <form className={classe} {...resto}>
      {children}
    </form>
  );
}

export function Campo({
  rotulo,
  ajuda,
  erro,
  children,
}: {
  rotulo: string;
  ajuda?: ReactNode;
  erro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{rotulo}</label>
      {children}
      {erro ? (
        <div className="help" style={{ color: 'var(--red-lt)' }} role="alert">
          {erro}
        </div>
      ) : (
        ajuda && <div className="help">{ajuda}</div>
      )}
    </div>
  );
}
